import React, { useState } from "react";
import useSessionStorage from "./useSessionStorage";
import { useLocation } from "react-router-dom";

import "./NavBar.css";

import { useNavigate } from "react-router-dom";

import ajb from "./images/ajb.png";

// Function to get cached image data
const getCachedImageData = (imageUrl) => {
  try {
    return localStorage.getItem(`cachedImage_${imageUrl}`);
  } catch (error) {
    console.error('Error reading cached image data:', error);
    return null;
  }
};

const NavBar = ({ visible, menus, version, logout, toggleFullscreen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tenant] = useSessionStorage("tenant");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMenuClick = (url) => {
    navigate(url);
    setMobileMenuOpen(false); // Close menu after navigation
  };

  // Get cached tenant info immediately and synchronously - always prioritize this to prevent flash
  const getCachedTenant = () => {
    try {
      const cached = localStorage.getItem('cachedTenantInfo');
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error parsing cached tenant info:', error);
      return null;
    }
  };

  // Get the effective tenant - use cached first to prevent flash, then session, then wait
  const cachedTenant = getCachedTenant();
  const effectiveTenant = cachedTenant || tenant;


  // Get the effective image source - prioritize ANY cached tenant image to eliminate flash
  const getEffectiveImageSource = (iconUrl) => {
    // First, check if we have ANY cached tenant images (even without knowing the URL)
    const cachedKeys = Object.keys(localStorage).filter(key => key.startsWith('cachedImage_'));
    if (cachedKeys.length > 0) {
      const cachedImageData = localStorage.getItem(cachedKeys[0]);
      if (cachedImageData) {
        return cachedImageData;
      }
    }
    
    // Fallback: if we have the icon URL, try to get cached data for it specifically
    if (iconUrl) {
      const cachedImageData = getCachedImageData(iconUrl);
      if (cachedImageData) return cachedImageData;
      return iconUrl;
    }
    
    // Last resort: show AJB logo
    return ajb;
  };

  if (visible === false) {
    return null;
  }

  // Check if we have any cached tenant images available
  const hasCachedImage = Object.keys(localStorage).some(key => key.startsWith('cachedImage_'));

  // CRITICAL: Only render navbar if we have tenant data OR cached images
  // This prevents showing AJB logo before any tenant data/images load
  if (!effectiveTenant && !hasCachedImage) {
    return null;
  }

  // If we have cached images but no tenant metadata, create minimal tenant info
  const displayTenant = effectiveTenant || { 
    name: 'Loading...', 
    icon: null // getEffectiveImageSource will handle finding cached image
  };

  const handleKeyPress = (event, action) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  return (
    <nav
      className="nav-bar"
      style={{ display: visible ? "flex" : "none" }}
      role="navigation"
      aria-label="Main navigation"
    >
      <ul className="nav-list nav-left">
        <li>
          <img
            src={getEffectiveImageSource(displayTenant.icon)}
            onClick={toggleFullscreen}
            onKeyPress={(e) => handleKeyPress(e, toggleFullscreen)}
            alt={`${displayTenant.name || 'Tenant'} logo`}
            tabIndex="0"
            role="button"
            aria-label="Toggle fullscreen"
          />
        </li>
        <li className="tenant-name">
          <b>{displayTenant.name || 'Tenant'}</b>
        </li>
      </ul>

      <button
        className="hamburger-menu"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
        aria-expanded={mobileMenuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <ul className={`nav-list nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {menus.map((x) => (
          <li key={x.label}>
            <a
              href={x.url}
              style={{
                fontWeight: location.pathname === x.url ? "bold" : "",
                color: "#FFF",
                textDecoration: "none",
              }}
              onClick={(e) => {
                e.preventDefault();
                handleMenuClick(x.url);
              }}
              aria-current={location.pathname === x.url ? "page" : undefined}
            >
              {x.label}
            </a>
          </li>
        ))}
        {tenant?.remoteAccessHelpUrl && (
          <li>
            <a
              href={tenant.remoteAccessHelpUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#FFF",
                textDecoration: "none",
              }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Remote Help
            </a>
          </li>
        )}
        <li>
          <a
            href="#"
            style={{
              color: "#FFF",
              textDecoration: "none",
            }}
            onClick={(e) => {
              e.preventDefault();
              logout();
              setMobileMenuOpen(false);
            }}
            role="button"
            aria-label="Logout"
          >
            Logout
          </a>
        </li>
      </ul>

      <ul className="nav-list nav-right">
        <li style={{ marginRight: "20px" }}>
          <img
            src={ajb}
            onClick={toggleFullscreen}
            onKeyPress={(e) => handleKeyPress(e, toggleFullscreen)}
            alt="AJB Software logo"
            tabIndex="0"
            role="button"
            aria-label="Toggle fullscreen"
          />
        </li>
      </ul>
    </nav>
  );
};

export default NavBar;
